import api from './api';

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface RegisterData {
  username: string; 
  email: string; 
  password: string;
  fullName?: string;
}

// Backend token response
export interface TokenResponse {
  access_token: string;
  token_type: string;
}

// User profile response from /auth/me endpoint
export interface UserResponse {
  id: string;
  username: string;
  email: string;
  full_name?: string;
  is_active: boolean;
}

// Combined data structure after processing token and fetching user data
export interface LoginResult {
  token: string;
  user: {
    id: string;
    username: string;
    email: string;
    fullName: string;
  }
}

export interface RegisterResponse {
  id: string;
  username: string;
  email: string;
  full_name?: string;
  is_active: boolean;
}

/**
 * Login a user with username/email and password
 */
export const login = async (credentials: LoginCredentials): Promise<LoginResult> => {
  try {
    // Step 1: Get token from login endpoint - OAuth2 expects form data, not JSON
    const formData = new URLSearchParams();
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);
    
    const tokenResponse = await api.post<TokenResponse>('/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    
    const token = tokenResponse.data.access_token;
    
    // Step 2: Set token in authorization header for subsequent requests
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    // Step 3: Get user data using the token
    const userResponse = await api.get<UserResponse>('/auth/me');
    
    // Step 4: Return combined result
    return {
      token: token,
      user: {
        id: userResponse.data.id,
        username: userResponse.data.username,
        email: userResponse.data.email,
        fullName: userResponse.data.full_name || '',
      }
    };
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * Register a new user
 */
export const register = async (userData: RegisterData) => {
  try {
    const response = await api.post<RegisterResponse>('/auth/register', {
      username: userData.username,
      email: userData.email,
      password: userData.password,
      full_name: userData.fullName,
    });
    
    return {
      success: true,
      user: {
        id: response.data.id,
        username: response.data.username,
        email: response.data.email,
        fullName: response.data.full_name || '',
      }
    };
  } catch (error) {
    console.error('Registration error:', error);
    throw error;
  }
};

/**
 * Login with GitHub OAuth
 */
export const githubLogin = async (code: string): Promise<LoginResult> => {
  try {
    // Step 1: Exchange code for token
    const tokenResponse = await api.post<TokenResponse>('/auth/github/login', 
      { code },
    );
    
    const token = tokenResponse.data.access_token;
    
    // Step 2: Set token in authorization header for subsequent requests
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    // Step 3: Get user data using the token
    const userResponse = await api.get<UserResponse>('/auth/me');
    
    // Step 4: Return combined result
    return {
      token: token,
      user: {
        id: userResponse.data.id,
        username: userResponse.data.username,
        email: userResponse.data.email,
        fullName: userResponse.data.full_name || '',
      }
    };
  } catch (error) {
    console.error('GitHub login error:', error);
    throw error;
  }
};

/**
 * Get the current user's profile
 */
export const getCurrentUser = async () => {
  try {
    const response = await api.get('/users/me');
    return response.data;
  } catch (error) {
    console.error('Error fetching current user:', error);
    throw error;
  }
};

/**
 * Update the current user's profile
 */
export const updateProfile = async (userData: Partial<{
  email: string;
  full_name: string;
  password: string;
}>) => {
  try {
    const response = await api.put('/users/me', userData);
    return response.data;
  } catch (error) {
    console.error('Error updating profile:', error);
    throw error;
  }
};
